import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Paypal from "components/Paypal";
import { recordBoostTransaction } from "store/actions/boostAction";

const MIN_DAYS = 1;
const MAX_DAYS = 7;
const MIN_PRICE = 2;
const MAX_PRICE = 599;

const AthletePromotionModal = ({ isOpen, onClose, athlete, type, onPurchaseSuccess }) => {
  const dispatch = useDispatch();
  const { uid, user } = useSelector((state) => state.auth);
  const [duration, setDuration] = useState(1);
  const [pricePerDay, setPricePerDay] = useState(MIN_PRICE);
  const [priceInput, setPriceInput] = useState(String(MIN_PRICE));
  const [isDone, setIsDone] = useState(false);
  const [opinion, setOpinion] = useState("");

  const totalPrice = duration * pricePerDay;
  const PAYPAL_FEE = 0.50;
  const chargeTotal = totalPrice + PAYPAL_FEE;

  const getModalTitle = () => {
    switch (type) {
      case "boost":          return "Boost Athlete";
      case "shoutout":       return "Boost Athlete with Opinion";
      case "selfBoost":      return "Self Boost";
      case "promoteManager": return "Promote Manager";
      default:               return "Promote";
    }
  };

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setDuration(1);
      setPricePerDay(MIN_PRICE);
      setPriceInput(String(MIN_PRICE));
      setIsDone(false);
      setOpinion("");
    }
  }, [isOpen]);

  const handlePriceChange = (e) => {
    const raw = e.target.value;
    setPriceInput(raw);
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= MIN_PRICE && parsed <= MAX_PRICE) {
      setPricePerDay(parsed);
    }
  };

  const handlePriceBlur = () => {
    const parsed = parseInt(priceInput, 10);
    if (isNaN(parsed) || parsed < MIN_PRICE) {
      setPricePerDay(MIN_PRICE);
      setPriceInput(String(MIN_PRICE));
    } else if (parsed > MAX_PRICE) {
      setPricePerDay(MAX_PRICE);
      setPriceInput(String(MAX_PRICE));
    } else {
      setPricePerDay(parsed);
      setPriceInput(String(parsed));
    }
  };

  // Called by Paypal component after successful capture
  const handlePaypalSuccess = (order) => {
    if (type === "shoutout" && !opinion.trim()) {
      toast.warning("Please enter your opinion before paying.");
      return;
    }

    const paymentId = order.id;
    const paidAmount = parseFloat(order.purchase_units[0].amount.value);

    dispatch(
      recordBoostTransaction({
        ...(type === "selfBoost"
          ? { managerID: athlete?.id || "" }
          : { athleteId: athlete?.id || "" }
        ),
        athleteName:  athlete?.name  || "",
        athleteEmail: athlete?.email || "",
        userId:   uid,
        fanName:  user?.name || "Anonymous",
        featureType: type,
        duration,
        pricePerDay,
        totalAmount:   paidAmount,
        paymentId,
        paymentMethod: "paypal",
        ...(type === "shoutout" && { opinion: opinion.trim() }),
      })
    );

    toast.success(`Boost confirmed for ${duration} day${duration > 1 ? "s" : ""}!`);
    setIsDone(true);
    if (onPurchaseSuccess) onPurchaseSuccess({ paymentId, duration, pricePerDay, totalAmount: paidAmount });
  };

  // ─── WEB3 purchase handler — commented out ───────────────────────────────

  return (
    <Modal isOpen={isOpen} toggle={!isDone ? onClose : undefined} size="md" centered>
      <ModalHeader toggle={!isDone ? onClose : undefined}>
        <span style={{ fontSize: "1.2rem", fontWeight: "600" }}>{getModalTitle()}</span>
      </ModalHeader>

      <ModalBody>
        {/* Athlete info */}
        {athlete && (
          <div className="text-center mb-3">
            {athlete.profileImage && (
              <img
                src={athlete.profileImage}
                alt={athlete.name || "Athlete"}
                className="rounded-circle mb-2"
                style={{ width: 72, height: 72, objectFit: "cover" }}
              />
            )}
            {athlete.name && <p className="mb-0" style={{ fontSize: "1rem", fontWeight: "600" }}>{athlete.name}</p>}
            {athlete.sport && <p className="text-muted mb-0" style={{ fontSize: "1rem" }}>{athlete.sport}</p>}
          </div>
        )}

        {!isDone ? (
          <>
            {/* Duration */}
            <FormGroup>
              <Label for="duration" style={{ fontSize: "1rem" }}><strong>Duration (Days)</strong></Label>
              <Input
                type="select"
                id="duration"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {Array.from({ length: MAX_DAYS - MIN_DAYS + 1 }, (_, i) => i + MIN_DAYS).map((day) => (
                  <option key={day} value={day}>
                    {day} {day === 1 ? "Day" : "Days"}
                  </option>
                ))}
              </Input>
            </FormGroup>

            {/* Price per day */}
            <FormGroup>
              <Label for="price">
                <strong>Price per Day ($2 – $599)</strong>
              </Label>
              <Input
                type="number"
                id="price"
                min={MIN_PRICE}
                max={MAX_PRICE}
                value={priceInput}
                onChange={handlePriceChange}
                onBlur={handlePriceBlur}
              />
            </FormGroup>

            {/* Total */}
            <FormGroup>
              <Label><strong>Total</strong></Label>
              <Input
                type="text"
                value={`$${chargeTotal.toFixed(2)} (includes $0.50 PayPal fee)`}
                disabled
                style={{ backgroundColor: "#e9ecef", cursor: "not-allowed", fontWeight: "bold" }}
              />
            </FormGroup>

            {/* Opinion — only for shoutout */}
            {type === "shoutout" && (
              <FormGroup>
                <Label for="opinion"><strong>Your Opinion</strong></Label>
                <Input
                  type="textarea"
                  id="opinion"
                  rows={4}
                  placeholder="Share your opinion about this athlete..."
                  value={opinion}
                  onChange={(e) => setOpinion(e.target.value)}
                  maxLength={500}
                />
                <small className="text-muted">{opinion.length}/500</small>
              </FormGroup>
            )}

            {/* PayPal button — only render when price is valid */}
            {pricePerDay >= MIN_PRICE && pricePerDay <= MAX_PRICE && (
              <div className="mt-3">
                {type === "shoutout" && !opinion.trim() ? (
                  <p className="text-muted text-center" style={{ fontSize: "0.875rem" }}>
                    Please enter your opinion above to enable payment.
                  </p>
                ) : (
                  <Paypal
                    price={chargeTotal.toFixed(2)}
                    getPaypalDetail={handlePaypalSuccess}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-3">
            <p className="text-success font-weight-bold mb-1" style={{ fontSize: "1.25rem" }}>Payment successful!</p>
            <p className="text-muted" style={{ fontSize: "1rem", color: "black" }}>
              {athlete?.name} boosted for {duration} day{duration > 1 ? "s" : ""} · ${chargeTotal.toFixed(2)}
            </p>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {isDone ? "Close" : "Cancel"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AthletePromotionModal;
